import React, { useState, useEffect, useRef } from 'react'
import clsx from 'clsx'
import FocusTrap from 'focus-trap-react'

// components
import Button from '/react4xp/components/Button'
import Guidelines from '/react4xp/components/Guidelines'
import Clock from '/react4xp/components/Clock'
import SearchInput from '/react4xp/components/SearchInput'

// utisl
import { tableWrapper } from '/lib/reactutils/utils'

interface HeaderProps {
    bgColor?: 'transparent' | 'primary' | 'white' | string;
    logoColor?: 'text-light' | 'text-dark' | string;
    isPrototype?: boolean;
    skipToMain: string;
    logoSvg: string;
    logoHref: string;
    logoAriaLabel: string;
    topMenu: Array<{ name: string, href: string, external?: boolean }>;
    searchHref: string;
    searchText: string;
    menuOpen: string;
    menuClose: string;
    menuFirstItems: Array<{
        name: string;
        link?: string;
        external?: boolean;
        subTopics: Array<{
            name: string;
            link: string;
            external?: boolean
        }>
    }>;
    menuSecondItems: Array<{ name: string, link: string, external?: boolean }>;
    clocks: Array<{ label: string, zone: string, background: string }>;
    custormerPortal: { name: string, link: string, external?: boolean };
}

export default function Header({
    bgColor,
    logoColor,
    isPrototype,
    skipToMain,
    logoSvg,
    logoHref,
    logoAriaLabel,
    topMenu,
    searchHref,
    searchText,
    menuOpen,
    menuClose,
    menuFirstItems,
    menuSecondItems,
    clocks,
    custormerPortal
}: HeaderProps) {

    // table wrapper function
    tableWrapper()

    const searchInputRef = useRef<HTMLInputElement>(null)
    const [isMenuShown, setIsMenuShown] = useState(false)
    const [isSearchActive, setIsSearchActive] = useState(false)
    const [menuColor, setMenuColor] = useState(bgColor || '')
    const [scrollPosition, setScrollPosition] = useState(0)
    const [firstSectionBgColor, setFirstSectionBgColor] = useState('')
    const [isDarkColor, setIsDarkColor] = useState(false)

    const openCloseMenu = () => {

        if (!isMenuShown && isSearchActive) {
            setIsSearchActive(false)
        }

        setScrollPosition(window.scrollY)

        setIsMenuShown(prev => {
            const newState = !prev

            if (newState) {
                window.document.body.style.overflowY = 'scroll'
            } else {
                window.document.body.style.overflowY = 'auto'
            }

            return newState
        })
    }

    const openCloseSearch = (event: React.MouseEvent) => {
        event.preventDefault()

        // close the menu if it's open
        if (!isSearchActive && isMenuShown) {
            setIsMenuShown(false)
        }
        
        // save current scroll position
        setScrollPosition(window.scrollY)

        setIsSearchActive(prev => {
            const newState = !prev

            if (newState) {
                window.document.body.style.overflowY = 'scroll'
            } else {
                window.document.body.style.overflowY = 'auto'
            }

            return newState
        })
    }

    useEffect(() => {
        let lastScrollTop = 0
    
        const handleScroll = () => {
            if (!isMenuShown && !isSearchActive) {
                const menu = window.document.getElementById('j-menu')
                const header = window.document.querySelector('header')
    
                // show / hide header based on scroll direction
                const isMobile = window.matchMedia('(max-width: 767px)').matches

                let currentScroll = 0

                if (isMobile) {
                    currentScroll = window.pageYOffset || window.document.documentElement.scrollTop
                }
    
                // scrolling down
                if (currentScroll > lastScrollTop) {
                    header.classList.add('j-menu--hidden')
                // scrolling up
                } else {
                    header.classList.remove('j-menu--hidden')
                }
    
                lastScrollTop = currentScroll <= 0 ? 0 : currentScroll
    
                // manage j-menu class
                if (window.scrollY > 0) {
                    menu.classList.add('j-menu--expanded')
                } else {
                    menu.classList.remove('j-menu--expanded')
                }
    
                updateMenuColor()
            }
        }

        const menu = window.document.getElementById('j-menu')
        
        if (isMenuShown || isSearchActive) {
            menu.classList.remove('j-menu--expanded')
        } else if (window.scrollY > 0) {
            menu.classList.add('j-menu--expanded')
        } else {
            menu.classList.remove('j-menu--expanded')
        }

        const updateMenuColor = () => {
            const sections = window.document.querySelectorAll('section')
            let currentBgColor = ''

            sections.forEach(section => {
                const rect = section.getBoundingClientRect()
                const isMobile = window.matchMedia('(max-width: 767px)').matches
                const isTablet = window.matchMedia('(min-width: 768px)').matches
                const measureMobile = isMobile && 60
                const measureTablet = isTablet && 36

                if (rect.top <= measureMobile && rect.bottom >= 0) {
                    const sectionBgColor = window.getComputedStyle(section).backgroundColor

                    if (sectionBgColor !== 'rgba(0, 0, 0, 0)' && sectionBgColor !== 'transparent') {
                        currentBgColor = sectionBgColor
                    }
                }

                if (rect.top <= measureTablet && rect.bottom >= measureTablet) {
                    const sectionBgColor = window.getComputedStyle(section).backgroundColor

                    if (sectionBgColor !== 'rgba(0, 0, 0, 0)' && sectionBgColor !== 'transparent') {
                        currentBgColor = sectionBgColor;
                    }
                }

            })

            // if no valid section background color is found, check <main>
            if (!currentBgColor || currentBgColor === 'rgba(0, 0, 0, 0)' || currentBgColor === 'transparent') {
                const mainElement = window.document.getElementById('main')

                if (mainElement) {
                    const mainBgColor = window.getComputedStyle(mainElement).backgroundColor

                    if (mainBgColor !== 'rgba(0, 0, 0, 0)' && mainBgColor !== 'transparent') {
                        currentBgColor = mainBgColor
                    }
                }
            }

            // if no valid background is found in <main>, check <body>
            if (!currentBgColor || currentBgColor === 'rgba(0, 0, 0, 0)' || currentBgColor === 'transparent') {
                const bodyBgColor = window.getComputedStyle(document.body).backgroundColor

                currentBgColor = bodyBgColor !== 'rgba(0, 0, 0, 0)' && bodyBgColor !== 'transparent'
                    ? bodyBgColor
                    : 'transparent'
            }
        
            setMenuColor(currentBgColor)
        }

        window.addEventListener('scroll', handleScroll)
        updateMenuColor()

        return () => window.removeEventListener('scroll', handleScroll)
    }, [isMenuShown, isSearchActive])

    useEffect(() => {
        setFirstSectionBgColor(window.document.querySelector('section') ? window.getComputedStyle(window.document.querySelector('section')).backgroundColor : '')
        setIsDarkColor([
            '#001F42',
            '#002855',
            'rgb(0, 31, 66)'
        ].includes(firstSectionBgColor) || ['#001F42', '#002855'].includes(rgbToHex(firstSectionBgColor)))
        
        // prevent automatic scroll restoration
        history.scrollRestoration = 'manual'

        // restore default behavior on cleanup
        return () => {
            history.scrollRestoration = 'auto'
        }
    }, [])

    // focus the input field
    useEffect(() => {
        if (isSearchActive && searchInputRef.current) {
            searchInputRef.current.focus()
        }
    }, [isSearchActive])

    const rgbToHex = (rgb: string) => {
        const rgbValues = rgb.match(/\d+/g)
        return rgbValues ? `#${rgbValues.map(x => parseInt(x).toString(16).padStart(2, '0')).join('')}`.toUpperCase() : ''
    }

    const isActiveColor = [
        '#001F42',
        '#002855',
        'rgb(0, 31, 66)'
    ].includes(menuColor) || ['#001F42', '#002855'].includes(rgbToHex(menuColor))

    return (
        <>
            {isPrototype && <Guidelines />}

            <Button
                href='#main'
                className='skip-to-main'
            >
                {skipToMain}
            </Button>

            <FocusTrap active={isMenuShown} focusTrapOptions={{ escapeDeactivates: false, clickOutsideDeactivates: false }}>

                <header id='j-menu' className='j-menu' style={menuColor ? { backgroundColor: menuColor } : null}>
                    <div className={clsx(
                        'grid-container relative j-menu__navbar',
                        (isMenuShown || isSearchActive) && 'j-menu__navbar--active',
                        isActiveColor && 'j-menu__navbar--light',
                        isDarkColor && 'j-menu__navbar--light',
                    )} style={isSearchActive ? { zIndex: 1001 } : null}>
                        <div className="grid-row">
                            <div className="c-12 j-menu__wrapper">
                                <a className={clsx('j-menu__logo')} href={logoHref} id='top-menu-logo' aria-label={logoAriaLabel}>
                                    <span
                                        dangerouslySetInnerHTML={{ __html: logoSvg }}
                                        suppressHydrationWarning
                                    />
                                </a>

                                <div className='j-menu__actions'>
                                    <ul>
                                        {topMenu.map((item, i) => (
                                            <li key={i}>
                                                <Button
                                                    href={item.href}
                                                    target={item.external}
                                                    menuBtn='pages'
                                                    className={clsx('text-dark labels-lg-regular hover-underline')}
                                                >
                                                    {item.name}
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className='j-menu__icons'>
                                        {/* BUTTON SEARCH */}
                                        <Button
                                            href={searchHref}
                                            rightIcon={isSearchActive ? 'Times' : 'Search'}
                                            className={clsx('j-menu__search-button', isSearchActive && 'active j-menu__closeSearch')}
                                            menuBtn='pages'
                                            onClick={openCloseSearch}
                                            ariaLabel='Open Search'
                                        />
                                        {/* BUTTON MENU  HAMBURGUER */}
                                        <Button
                                            className={clsx('j-menu__hamburguer', isMenuShown && 'active j-menu__closeHamburguer')}
                                            rightIcon={isMenuShown ? 'Times' : 'Hamburguer'}
                                            menuBtn='pages'
                                            onClick={openCloseMenu}
                                            ariaLabel='Open menu'
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>


                    <div id='j-menu__drawer' className={clsx('j-menu__drawer', isMenuShown ? 'j-menu__drawer--active bgcolor' : '')}>
                        <div className='grid-container'>
                            <div className='grid-row j-menu__inner-drawer'>
                                <div className="c-12 c-md-4 j-menu__items">
                                    {menuFirstItems.map((topic, i) => (
                                        <div key={i} className='j-menu__primaries'>

                                            {topic.link ? (
                                                <Button
                                                    href={topic.link}
                                                    target={topic.external}
                                                    menuBtn='topic'
                                                    className='hover-underline'
                                                >
                                                    {topic.name}
                                                </Button>
                                            ) : (
                                                <p className='btn btn__menu--topic'>
                                                    {topic.name}
                                                </p>
                                            )}

                                            {topic.subTopics.length > 0 && (
                                                <ul className='j-menu__sub-items'>
                                                    {topic.subTopics.map((subTopic, j) => (
                                                        <li key={j}>
                                                            <Button
                                                                href={subTopic.link}
                                                                target={subTopic.external}
                                                                menuBtn='sub-topic'
                                                                className='hover-underline'
                                                            >
                                                                {subTopic.name}
                                                            </Button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="c-xs-6 start-md-5 c-md-3 j-menu__items">
                                    <ul className='j-menu__secondary-items'>
                                        {menuSecondItems.slice(0, 4).map((item, i) => (
                                            <li key={i}>
                                                <Button
                                                    href={item.link}
                                                    target={item.external}
                                                    menuBtn='sub-topic'
                                                    className='hover-underline'
                                                >
                                                    {item.name}
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="c-xs-6 c-md-3 j-menu__items">
                                    <ul className='j-menu__secondary-items'>
                                        {menuSecondItems.slice(4, 8).map((item, i) => (
                                            <li key={i}>
                                                <Button
                                                    href={item.link}
                                                    target={item.external}
                                                    menuBtn='sub-topic'
                                                    className='hover-underline'
                                                >
                                                    {item.name}
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                {
                                    custormerPortal.name &&
                                    <div className="c-12 c-md-2 j-menu__items j-menu__button">
                                        <Button
                                            color='light'
                                            size='md'
                                            className='btn__nobreak'
                                            rightIcon=''
                                            href={custormerPortal.link}
                                            target={custormerPortal.external}
                                            >
                                            {custormerPortal.name}
                                        </Button>
                                    </div>
                                }
                                <div className="c-md-8 j-menu__divider">

                                </div>
                                <div className="start-md-5 c-md-8 j-menu__clocks">
                                    {clocks.map((clock, i) => (
                                        <Clock
                                            key={i}
                                            background={clock.background}
                                            label={clock.label}
                                            zone={clock.zone}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Search */}

                    <div id='j-menu__search' className={clsx('j-menu__search', isSearchActive && 'j-menu__search--active')}>
                        <div className='grid-container'>
                            <div className='grid-row j-menu__search-inner'>
                                <div className="c-12 j-menu__search-form">
                                    <SearchInput
                                        ref={searchInputRef} // Pass the ref to SearchInput
                                        className='j-menu__search-input'
                                        leftIcon='Search'
                                        rightIcon='ArrowRight'
                                        placeholder='Search'
                                        href={searchHref}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

            </FocusTrap>
        </>
    );
}

